import { MappingType_Enum } from '@weave/schema-gen-ts/dist/schemas/syncapp/mappings/v1/service.pb';
import { useQueries } from 'react-query';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ServiceProvidersConstants } from '@frontend/api-service-providers';
import { SchemaMappingsService } from '@frontend/schema';
import { queryKeys } from '../../query-keys';
import { NON_INTEGRATED_APPT_STATUS_LIST } from '../components/v3/EventCardAppointmentStatusChip/helpers';
import { AppointmentStatusV3Enum } from '../components/v3/EventCardAppointmentStatusChip/types';

type UseGetAppointmentStatusesProps = {
  locationIds: string[];
  enabled?: boolean;
};

const { ONE_MINUTE_IN_MILLISECONDS } = ServiceProvidersConstants;

export const APPT_STATUS_ALL_OPTION = 'all';

export const useGetAppointmentStatusList = ({ locationIds }: UseGetAppointmentStatusesProps) => {
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  const queryResult = useQueries(
    locationIds.map((locationId) => ({
      queryKey: queryKeys.listAppointmentStatuses(locationId),
      queryFn: async () => await SchemaMappingsService.ListMappings({ locationId, type: MappingType_Enum.APPOINTMENT }),
      retry: 1,
      cacheTime: 5 * ONE_MINUTE_IN_MILLISECONDS, // 5 mins
      staleTime: 5 * ONE_MINUTE_IN_MILLISECONDS, // 5 mins
      enabled: !!locationId,
    }))
  );

  const statusList = queryResult
    .map((result) => {
      if (result.isSuccess) {
        return result.data.mappings;
      }
      return [];
    })
    .flat()
    .filter(Boolean);

  // Flatten the array of arrays and convert to Set to remove duplicates
  const uniqueStatusList = Array.from(
    new Set([...statusList.flatMap(({ mappings }) => mappings.map(({ key }) => key.toLowerCase()))])
  );

  if (!isIntegratedOffice && isScheduleV3FlagEnabled) {
    const v3Statuses = NON_INTEGRATED_APPT_STATUS_LIST.filter(
      (status) =>
        status.key !== AppointmentStatusV3Enum.CANCELED && status.key !== AppointmentStatusV3Enum.UNKNOWN_EVENT_STATUS
    ).flatMap((status) => status.text.toLowerCase());
    return [APPT_STATUS_ALL_OPTION, ...v3Statuses.slice().sort()];
  }

  return !!uniqueStatusList.length ? [APPT_STATUS_ALL_OPTION, ...uniqueStatusList.slice().sort()] : [];
};
